import { create } from "zustand";
import type { Theme } from "./types";
import { applyPalette } from "./theme/apply";

export type Language = "th" | "en";
// Which lab pane has the floor. null = the normal split. Closing one pane and
// making the other full screen are the same state reached from two directions,
// so it is one value rather than a pair of booleans that can disagree.
export type LabFocus = "guide" | "workspace" | null;
type UIState = { theme: Theme; language: Language; railCollapsed: boolean; docsSidebarCollapsed: boolean; docsOutlineCollapsed: boolean; guideWidth: number; labFocus: LabFocus; fontScale: number; setTheme: (v: Theme) => void; setLanguage: (v: Language) => void; toggleRail: () => void; toggleDocsSidebar: () => void; toggleDocsOutline: () => void; setGuideWidth: (v: number) => void; setLabFocus: (v: LabFocus) => void; setFontScale: (v: number) => void; stepFontScale: (delta: number) => void; };
// localStorage can throw or be unavailable (private browsing, storage disabled, some test
// environments) — every access goes through these so a broken storage backend just falls
// back to defaults instead of crashing the whole module (and everything that imports it).
const storageGet = (key: string): string | null => { try { return localStorage.getItem(key); } catch { return null; } };
const storageSet = (key: string, value: string): void => { try { localStorage.setItem(key, value); } catch { /* ignore */ } };
// The floor keeps the 0.8125em code blocks above ~11px; the ceiling keeps an
// 80ch measure from overflowing the guide pane at its narrowest.
const clampFontScale = (value: number): number => {
  const safe = Math.max(0.85, Math.min(1.5, Math.round(value * 100) / 100));
  storageSet("workshop-font-scale", String(safe));
  return safe;
};
const savedTheme = (): Theme => storageGet("workshop-theme") === "light" ? "light" : "dark";
const savedLanguage = (): Language => storageGet("workshop_lang") === "en" ? "en" : "th";
const initialTheme = savedTheme();
const initialLanguage = savedLanguage();
// Before React renders: the stylesheet ships the dark values as a static
// fallback, so this only matters when the saved preference is light — without it
// a light-mode reload would flash dark.
document.documentElement.dataset.theme = initialTheme;
applyPalette(initialTheme);
document.documentElement.lang = initialLanguage;

export const useUI = create<UIState>((set) => ({
  theme: initialTheme,
  language: initialLanguage,
  railCollapsed: storageGet("workshop-rail") === "collapsed",
  docsSidebarCollapsed: storageGet("workshop-docs-sidebar") === "collapsed",
  docsOutlineCollapsed: storageGet("workshop-docs-outline") === "collapsed",
  guideWidth: Number(storageGet("workshop-guide-width") || 42),
  // Reading size for the guide pane and the docs reader. Persisted: someone who
  // needs larger text needs it on every page and on the next day of the class,
  // not once per session.
  fontScale: Number(storageGet("workshop-font-scale") || 1),
  // Not persisted: a fresh page load should always start on the normal split,
  // never reopen already collapsed into a pane someone left full-screen.
  labFocus: null,
  setTheme: (theme) => { document.documentElement.dataset.theme = theme; applyPalette(theme); storageSet("workshop-theme", theme); set({ theme }); },
  setLanguage: (language) => { document.documentElement.lang = language; storageSet("workshop_lang", language); document.dispatchEvent(new CustomEvent("workshop:lang", { detail: { lang: language } })); set({ language }); },
  toggleRail: () => set((s) => { const railCollapsed = !s.railCollapsed; storageSet("workshop-rail", railCollapsed ? "collapsed" : "open"); return { railCollapsed }; }),
  toggleDocsSidebar: () => set((s) => { const docsSidebarCollapsed = !s.docsSidebarCollapsed; storageSet("workshop-docs-sidebar", docsSidebarCollapsed ? "collapsed" : "open"); return { docsSidebarCollapsed }; }),
  toggleDocsOutline: () => set((s) => { const docsOutlineCollapsed = !s.docsOutlineCollapsed; storageSet("workshop-docs-outline", docsOutlineCollapsed ? "collapsed" : "open"); return { docsOutlineCollapsed }; }),
  setGuideWidth: (guideWidth) => { const safe = Math.max(28, Math.min(62, guideWidth)); storageSet("workshop-guide-width", String(safe)); set({ guideWidth: safe }); },
  setLabFocus: (labFocus) => set({ labFocus }),
  // Clamped like setGuideWidth. The floor keeps the 0.8125em code blocks above
  // ~11px; the ceiling keeps an 80ch measure from overflowing the guide pane at
  // its narrowest.
  setFontScale: (value) => set({ fontScale: clampFontScale(value) }),
  // Steps from the CURRENT value rather than from whatever the button's render
  // closed over. With the closure version two quick clicks both computed from
  // the same stale scale and the second one did nothing.
  stepFontScale: (delta) => set((s) => ({ fontScale: clampFontScale(s.fontScale + delta) }))
}));
